import {
  BarChart3,
  CreditCard,
  FileText,
  LayoutDashboard,
  LayoutGrid,
  Package,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Store,
  Users,
  Wrench,
} from "lucide-react";
import type { ShopFeatureFlagId } from "@/modules/merchant/domain/contracts/shop-feature-flags";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";

/** รายการย่อยภายใต้เมนูพับได้ */
export type MerchantNavSubItem = {
  title: string;
  /** ใช้ `$shopId` แทน id ร้านจริงตอน navigate */
  url: string;
  icon?: React.ElementType;
  /** ถ้ามี = แสดงเฉพาะเมื่อ user มีสิทธิ์นี้ในร้าน */
  requiredMerchantPermission?: string;
  /** ถ้ามี = ร้านนี้ต้องเปิด feature flag (Admin) */
  requiredShopFeature?: ShopFeatureFlagId;
  badge?: string;
};

/** ลิงก์เดียว (ไม่มีเมนูย่อย) */
export type MerchantNavLinkItem = {
  title: string;
  url: string;
  icon?: React.ElementType;
  requiredMerchantPermission?: string;
  requiredShopFeature?: ShopFeatureFlagId;
  badge?: string;
};

/** เมนูพับได้ — มี `items` ไม่มี `url` */
export type MerchantNavCollapsibleItem = {
  title: string;
  icon?: React.ElementType;
  /** ถ้ามี = ต้องมีสิทธิ์นี้ถึงจะเห็นหัวข้อหลัก (ทางเลือก) */
  requiredMerchantPermission?: string;
  requiredShopFeature?: ShopFeatureFlagId;
  badge?: string;
  items: MerchantNavSubItem[];
};

export type MerchantNavItem = MerchantNavLinkItem | MerchantNavCollapsibleItem;

export function isMerchantNavCollapsible(
  item: MerchantNavItem,
): item is MerchantNavCollapsibleItem {
  return "items" in item && Array.isArray(item.items);
}

export const merchantSidebarGroups: {
  title: string;
  items: MerchantNavItem[];
}[] = [
  {
    title: "ທົ່ວໄປ",
    items: [
      {
        title: "ແຜງຄວບຄຸມ",
        url: "/app/shops/$shopId",
        icon: LayoutDashboard,
      },
    ],
  },
  {
    title: "ເມນູຫຼັກ",
    items: [
      {
        title: "ສິນຄ້າ",
        url: "/app/shops/$shopId/catalog",
        icon: Package,
        requiredMerchantPermission: MerchantPermissions.catalog.read,
      },
      {
        title: "ສາງ",
        url: "/app/shops/$shopId/inventory",
        icon: Store,
        requiredMerchantPermission: MerchantPermissions.inventory.read,
        requiredShopFeature: "inventory",
      },
      {
        title: "ໃບສັ່ງຊື້",
        url: "/app/shops/$shopId/purchase-orders",
        icon: ShoppingCart,
        requiredMerchantPermission: MerchantPermissions.inventory.read,
        requiredShopFeature: "procurement",
      },
      {
        title: "ຂາຍ",
        url: "/app/shops/$shopId/pos",
        icon: CreditCard,
        requiredMerchantPermission: MerchantPermissions.pos.read,
      },
      {
        title: "ງານສ້ອມ",
        icon: Wrench,
        requiredMerchantPermission: MerchantPermissions.workshop.read,
        requiredShopFeature: "workshop",
        items: [
          {
            title: "ບໍລິການ",
            url: "/app/shops/$shopId/services",
            icon: Wrench,
            requiredMerchantPermission: MerchantPermissions.catalog.read,
          },
          {
            title: "ກະດານວຽກ",
            url: "/app/shops/$shopId/workshop",
            icon: LayoutGrid,
            requiredMerchantPermission: MerchantPermissions.workshop.read,
            requiredShopFeature: "workshop",
          },
          {
            title: "ໃບປະເມີນລາຄາ",
            url: "/app/shops/$shopId/workshop/quotes",
            icon: FileText,
            requiredMerchantPermission: MerchantPermissions.workshop.read,
            requiredShopFeature: "workshop",
          },
        ],
      },
      {
        title: "ລາຍງານ",
        url: "/app/shops/$shopId/reports",
        icon: BarChart3,
        requiredMerchantPermission: MerchantPermissions.pos.read,
      },
    ],
  },
  {
    title: "ຕັ້ງຄ່າຮ້ານ",
    items: [
      {
        title: "ຕັ້ງຄ່າຮ້ານ",
        url: "/app/shops/$shopId/settings",
        icon: Settings,
      },
      {
        title: "ພະນັກງານ",
        url: "/app/shops/$shopId/staff",
        icon: Users,
        requiredMerchantPermission: MerchantPermissions.shop_staff.read,
      },
      {
        title: "ບົດບາດ",
        url: "/app/shops/$shopId/roles",
        icon: ShieldCheck,
        requiredMerchantPermission: MerchantPermissions.shop_roles.read,
      },
    ],
  },
];
